import _ from "lodash";
import fp from "lodash/fp";
import uuid from "uuid/v4";
import md5 from "md5";
import { IItemField, ItemFields } from "constants/product-collection";
import {
    AllocationDimension,
    AllocationDimensionIndex,
    AllocationDimensionOptions,
    IAllocationDimension,
} from "helpers/allocation-dimension";
import { PurchaseOrder } from "@nuorder/client-cart-api/dist/types/modules/order/models";
import { Schema, SchemaFieldSubtype } from "types/schema";
import { formatDateRange } from "components/shipment-review/helpers/formatDate";
import { isEqualWithDefined } from "../../objUtils";
import { format } from "lib/globalize-util";
import { SHIPMENTS_WITH_UNITS_OPTION } from "constants/shipments";
import { ALL_OPTION_GROUP } from "constants/types";
import { SizeData } from "components/shipment-review/types";
import type {
    IAvailability,
    IDateRange,
    IDeliveryRange,
    IGridEntry,
    ILocation,
    IOrderData,
    IDetailHeaderPrices,
    LineItemAttributes,
    IOrderCurrency,
} from "types/cart/shipment-review";
import { Warehouse } from "@nuorder/client-cart-api/dist/types/modules/brand/models";
import { getSupportedFDMFields, mapLineItemFDMField } from "containers/legacy-cart/helpers";
import { isAllocated } from "helpers/legacy-cart/bulk";
import { COLUMN_FIELDS } from "constants/sizingView";
import { InitialGroupOrderComparatorParams } from "ag-grid-community";
import { ICustomizationData } from "../../../constants/product-customizations/customization";
import { AllocationType } from "types/cart";
import { getDateKey } from "components/shipment-review/shipment-review-grid";
import type { IProduct } from "types/product";
import { getPricingMap } from "helpers/legacy-cart/line-items/getPricingMap";

/**
 * Returns the list of allocation dimensions -> i.e. delivery and size.
 * Doors are not applicable for legacy-cart since the consumers are not likely to be the big retailers.
 * Uses allocation-dimension-schema-base-template.json as template.
 *
 */
export const constructAllocationDimensionsFromPurchaseOrders = (
    purchaseOrders: PurchaseOrder[]
): IAllocationDimension[] => {
    if (purchaseOrders) {
        return [
            // delivery
            getAllocationDimension({
                id: `allocation_dimension_${uuid()}}`, // can be random number or 0
                name: "Deliveries",
                dimension_values: constructDeliveryDimensionsBySplits(purchaseOrders),
            }),
            // door
            getAllocationDimension({
                id: `allocation_dimension_${uuid()}`,
                name: "Size",
                dimension_values: [],
            }),
        ];
    }
    return [];
};
export const constructAllocationDimensions = (
    ranges: IDeliveryRange[],
    options?: AllocationDimensionOptions
): IAllocationDimension[] => {
    if (ranges) {
        return [
            // delivery
            getAllocationDimension({
                id: `allocation_dimension_${uuid()}}`, // can be random number or 0
                name: "Deliveries",
                dimension_values: constructDeliveryDimensions(ranges, options),
            }),
            // door
            getAllocationDimension({
                id: `allocation_dimension_${uuid()}`,
                name: "Size",
                dimension_values: [],
            }),
        ];
    }
    return [];
};

const getAllocationDimension = fp.defaultsDeep({
    id: "",
    revision: 2,
    name: "",
    subtype: "GENERIC",
    active: true,
    dimension_values: [],
    attributes: {
        enable_inventory: false,
        merchant_name: "",
        allocation_enabled: false,
        size_table_type: "DEFAULT",
        default_date_format: "",
        identifier_fields: [],
        keys: [],
        allow_duplicate_product_entries: false,
        units_entry_type: "TOTAL",
        size_allocation_type: "ROW",
    },
});

export enum AllocRowValues {
    DimensionId,
    Door,
    SizeGroupLabel,
    Quantity,
    WholesalePrice,
}

export enum BulkRowValues {
    DimensionId,
    Door,
    Quantity,
    TotalWholesalePrice,
}

export type AllocRow = [string, string, string, number, number];
export type BulkRow = [string, string, number, number];
export type Row<Cols extends any[]> = { c: Cols };

export type ItemRow<Row extends any[]> = { r: Row };

interface RowMetadata {
    currency?: IOrderCurrency;
    deliveryConfig?: IOrderData["deliveryConfig"];
    shipments?: IDateRange[];
    availability?: IAvailability;
}

export interface IGridItem extends RowMetadata {
    fields: IItemField;
    rowId: string;
    productId: string;
    entries: IGridEntry[];
    product: IProduct;
    pricesPerSizeMap?: IDetailHeaderPrices;
    location?: ILocation;
    shipment?: string;
    detailsRowsCount?: number;
    warehouses?: Warehouse[];
    attributes?: LineItemAttributes;
    customizations?: ICustomizationData;
    hasSingleDelivery?: boolean;
    options?: {
        hasCombined: boolean;
        hasLocations: boolean;
        hasError: boolean;
        isOverordered: boolean;
    };
}

interface ProductVariantAllocation extends Omit<IGridItem, "rowId"> {
    fdm: LineItemAttributes;
    prebook: boolean;
    shipment: string;
}

export const buildGridItems = (
    entries: IGridEntry[],
    options: {
        largeView?: boolean;
        productSchema?: Schema;
        orderSchema?: Schema;
        productSplit?: boolean;
        shipments?: IDateRange[];
        usePricingService?: boolean;
        usePricePerSize?: boolean;
        dbpPriceEnabled?: boolean;
        currency?: IOrderCurrency;
        shipmentGrouping?: boolean;
    },
    rowData?: RowMetadata
): IGridItem[] => {
    const { usePricingService = false, usePricePerSize = false, dbpPriceEnabled = false } = options || {};
    const allocations: ProductVariantAllocation[] = [];

    // fdm fields already shown in the grid within one product, and we should not split by them
    // TODO: remove after NU-55947 and NU-55943 will be implemented
    const skippedFdmFromSplit: string[] = getSupportedFDMFields(options.orderSchema?.fields).map(mapLineItemFDMField);

    // store all delivery map when shipmentGrouping is enabled
    const entriesDeliveryMap = {};

    // get fields from product schema. The fields are consistent for each of the product.
    _.forEach(entries, (entry: IGridEntry) => {
        const { prebook, location, cartLineItemId, product } = entry;
        const productId = product._id;
        const hideEntryPrice = usePricingService && !isAllocated(entry.allocated, entry.location);
        const retailPrice = hideEntryPrice ? 0 : entry.pricing?.retail;
        const dateKey = formatDateRange(entry.shipStart, entry.shipEnd, { collapseCurrentYear: true });
        // Add image_url which contains the thumbnail and is mandatory.
        const itemsFields: IItemField = {
            image_url: {
                string_value: options?.largeView ? product.imagesM?.[0] : product.imagesT?.[0],
            },
            id: { string_value: uuid() }, // used in aggrid row id,
            _line_item_id: { string_value: entry.cartLineItemId },
            _product_id: { string_value: productId },
            prebook: { bool_value: prebook },
            [COLUMN_FIELDS.LOCATION]: { string_value: entry.location?.name || "" },
            [COLUMN_FIELDS.SHIPMENT]: { string_value: options.shipmentGrouping ? dateKey : "" },
            wholesale_range: { range_value: hideEntryPrice ? [0, 0] : entry.pricing?.wholesale_range },
            original_wholesale_range: {
                range_value: hideEntryPrice ? [0, 0] : entry.pricing?.original_wholesale_range,
            },
            retail_range: { range_value: [retailPrice, retailPrice] },
        };

        const fdm = _.omit(entry.attributes, skippedFdmFromSplit);
        const uniqAllocKey = getAllocUniqKey(productId, fdm);
        entriesDeliveryMap[uniqAllocKey] ??= new Set();
        entriesDeliveryMap[uniqAllocKey].add(`${dateKey}-${cartLineItemId}`);

        options?.productSchema?.fields?.forEach(({ key, details }) => {
            const value = product?.[key] ?? "";
            itemsFields[key] = details?.subtype === "date" ? { date_value: value } : { string_value: value };
        });

        options?.orderSchema?.fields?.forEach((field) => {
            if (field.key?.startsWith("line_items.")) {
                const key = mapLineItemFDMField(field);
                // If the product lookup field is already present, avoid overriding any existing value
                const isExistingProductLookup =
                    itemsFields[key] &&
                    field.details?.subtype === SchemaFieldSubtype.Lookup &&
                    field.details?.lookup?.startsWith("product");
                if (!isExistingProductLookup) {
                    itemsFields[key] = { string_value: entry.attributes?.[key]?.value ?? "" };
                }
            }
        });

        const allocation = allocations.find(
            options?.productSplit
                ? (x) =>
                      x.productId === productId &&
                      isEqualWithDefined(_.omit(x.fdm, skippedFdmFromSplit), fdm) && // split items only by product data
                      (!options.shipmentGrouping || x.shipment === dateKey)
                : (x) =>
                      x.productId === productId &&
                      (!x.location || x.prebook === prebook) && // TODO: remove within NU-50181. Split by prebook for Bulk Order only
                      x.location?.id === location?.id &&
                      isEqualWithDefined(_.omit(x.fdm, skippedFdmFromSplit), fdm) &&
                      (!options.shipmentGrouping || x.shipment === dateKey)
        );
        const hasCombined = entry.allocated === AllocationType.Combined;
        const hasLocations = entry.allocated === AllocationType.ByLocation;

        if (!allocation) {
            allocations.push({
                shipment: options.shipmentGrouping ? dateKey : "",
                productId,
                fields: itemsFields,
                fdm: entry.attributes,
                prebook,
                entries: [entry],
                location: entry.location,
                customizations: entry.customization,
                options: { hasCombined, hasLocations, hasError: entry.hasError, isOverordered: entry.isOverordered },
                product: entry.product,
            });
            return;
        }
        allocation.options.hasCombined ||= hasCombined;
        allocation.options.hasLocations ||= hasLocations;
        allocation.options.hasError ||= entry.hasError;
        allocation.options.isOverordered ||= entry.isOverordered;

        const { fields, entries } = allocation;
        entries.push(entry);

        fields.retail_range.range_value = extendRange(fields.retail_range.range_value, retailPrice);
        fields.wholesale_range.range_value = getRange([
            ...(fields.wholesale_range.range_value || []),
            ...(itemsFields.wholesale_range.range_value || []),
        ]);
        if (fields.original_wholesale_range.range_value || itemsFields.original_wholesale_range.range_value) {
            fields.original_wholesale_range.range_value = getRange([
                ...(fields.original_wholesale_range.range_value || []),
                ...(itemsFields.original_wholesale_range.range_value || []),
            ]);
        }
    });

    return _.map(allocations, (value) => {
        // TODO: GOA - merge build order shipment with shipments from entries for cases when enforce available until is enabled
        const dates = (options.shipments && _.map(options.shipments, "dateKey")) || getDetailRowKeys(value.entries);
        const fdm = _.omit(value.fdm, skippedFdmFromSplit);
        const hasSingleDelivery = entriesDeliveryMap[getAllocUniqKey(value.productId, fdm)].size === 1;
        const pricesPerSizeMap = usePricePerSize && getPricingMap(value.entries, options?.currency);
        const hasPpsSizes = _.some(
            pricesPerSizeMap,
            (priceDetails) => !_.isEqual(priceDetails.prices, _.values(pricesPerSizeMap)[0].prices)
        );
        return {
            ...rowData,
            [ItemFields.Fields]: value.fields,
            location: value.location,
            entries: value.entries,
            productId: value.productId,
            warehouses: value.entries?.[0]?.availableWarehouses,
            attributes: value.fdm,
            shipment: value.shipment,
            customizations: value.customizations,
            hasSingleDelivery,
            pricesPerSizeMap: hasPpsSizes ? pricesPerSizeMap : null,
            options: value.options,
            detailsRowsCount: _.size(dates), // detail rows based on shipments
            product: value.product,
            rowId: md5(
                JSON.stringify({
                    hasSingleDelivery,
                    attributes: _.map(fdm, ({ value, hasError }) => ({ value, hasError })),
                    prebook: value.location ? value.prebook : undefined, // TODO: remove within NU-50181
                    productId: value.productId,
                    location: value.location?.id,
                    warehouses: _.map(value.entries?.[0]?.availableWarehouses, "id"), // Hack: Recalculate Detail Grid to hide/show Warehouse and Prebook columns
                    dates, // Hack: Recalculate Detail Grid to hide/show Warehouse and Prebook columns on dates changes
                    currency: options?.currency,
                    shipment: options.shipmentGrouping && value.shipment,
                    pricesPerSizeMap, // to recalculate details header prices on discount applied
                })
            ),
        };
    });
};

export const getDetailRowKeys = (entries: IGridEntry[]): string[] =>
    _.uniq(
        _.map(
            entries,
            ({ shipStart, shipEnd, cartLineItemId }) => `${getDateKey(shipStart, shipEnd)}_${cartLineItemId}`
        )
    );

const getAllocUniqKey = (productId: string, fdm: LineItemAttributes) =>
    `${productId} - ${JSON.stringify(_.pick(fdm, ["field", "value"]))}`;

const extendRange = ([min, max]: [number, number], n: number | null | undefined): [number, number] => [
    Math.min(min, n ?? Infinity),
    Math.max(max, n ?? 0),
];

export const getRange = (array: number[]): [number, number] | undefined =>
    _.isEmpty(array) ? undefined : [_.min(array), _.max(array)];

export const getQuantityWithUnitsPerPack = (num: number, unitsPerPack) => {
    return !unitsPerPack ? num : num * unitsPerPack;
};

const constructDeliveryDimensions = (
    ranges: IDeliveryRange[],
    options?: AllocationDimensionOptions
): AllocationDimension[] => {
    const dimensions = _.map(ranges, (order): AllocationDimension => {
        const id = formatDateRange(order.shipStart, order.shipEnd);
        return {
            active: true,
            included: true,
            id,
            name: id,
            group: AllocationDimensionIndex[AllocationDimensionIndex.Date],
        };
    });
    if (options?.shipmentsWithUnits) {
        dimensions.unshift({
            active: true,
            included: true,
            id: SHIPMENTS_WITH_UNITS_OPTION,
            name: format("Select shipments with units"),
            group: ALL_OPTION_GROUP,
        });
    }
    return _.uniqBy(dimensions, "id");
};

/** @deprecated constructDeliveryDimensions should be used instead*/
const constructDeliveryDimensionsBySplits = (purchaseOrders: PurchaseOrder[]): AllocationDimension[] => {
    return purchaseOrders.map((order) => ({
        active: true,
        included: true,
        id: order.id,
        name: formatDateRange(order.shipStart, order.shipEnd),
        group: AllocationDimensionIndex[AllocationDimensionIndex.Date],
    }));
};

export const hasAnySizeQuantities = fp.some<SizeData>(({ quantity }) => quantity > 0);

export const getLocationGroupSortComparator =
    (locationNames: string[]) =>
    ({ nodeA, nodeB }: InitialGroupOrderComparatorParams) =>
        _.every([nodeA.rowGroupColumn?.getColDef(), nodeB.rowGroupColumn?.getColDef()], {
            field: COLUMN_FIELDS.LOCATION,
        })
            ? _.indexOf(locationNames, nodeA.key) - _.indexOf(locationNames, nodeB.key)
            : undefined; // default group sorting
